// /src/Components/Clients/AdminWhiteLabels.jsx
import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import { BadgeDollarSign, RefreshCcw, ShieldCheck, ShieldOff } from "lucide-react";

import useClients from "../Services/ClientServices/useClient";

const PAGE_SIZE = 100;

export default function AdminWhiteLabels() {
  const { api, adminId } = useContext(GlobalContext);

  const { fetchAll, fetchCreditTopupReport, creditTopup, toggleClientStatus } =
    useClients();
  // geo (optional)
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  // data
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [q, setQ] = useState("");

  // paginate
  const [page, setPage] = useState(1);

  // topup modal
  const [topOpen, setTopOpen] = useState(false);
  const [topBusy, setTopBusy] = useState(false);
  const [topClient, setTopClient] = useState(null);
  const [topAmount, setTopAmount] = useState("");
  const [topRef, setTopRef] = useState("");
  const [topRemark, setTopRemark] = useState("");

  // report modal
  const [repOpen, setRepOpen] = useState(false);
  const [repBusy, setRepBusy] = useState(false);
  const [repClient, setRepClient] = useState(null);
  const [repRows, setRepRows] = useState([]);

  // confirm toggle modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmClient, setConfirmClient] = useState(null);
  const [confirmNext, setConfirmNext] = useState(""); // "Activate" or "Deactivate"

  // geo capture (best-effort)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
      },
      () => {} // silent
    );
  }, []);

  // fetch list
const fetchClients = async () => {
  try {
    setLoading(true);

    const data = await fetchAll({
      limit: 100,
      offset: 0,
    });

    setClients(Array.isArray(data) ? data : []);
  } catch (e) {
    console.error("fetchClients error:", e);
    toast.error("Error loading clients");
    setClients([]);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  // derived lists
  const filtered = useMemo(() => {
    const s = (v) => String(v ?? "").toLowerCase();
    const ql = q.trim().toLowerCase();
    return clients.filter(
      (c) =>
        !ql ||
        s(c.client_name).includes(ql) ||
        s(c.client_mobile).includes(ql) ||
        s(c.userType).includes(ql) ||
        s(c.onboardingType).includes(ql) ||
        s(c.onboardedBy).includes(ql)
    );
  }, [clients, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  // actions: open modals
  const openTopUp = (client) => {
    setTopClient(client);
    setTopAmount("");
    setTopRef("");
    setTopRemark("");
    setTopOpen(true);
  };

const openReport = async (client) => {
  setRepClient(client);
  setRepOpen(true);
  setRepBusy(true);

  try {
    const data = await fetchCreditTopupReport({
      adminId,
      userId: client.id,
    });

    if (data?.responseCode === "00") {
      setRepRows(data.data || []);
    } else {
      toast.error(
        data?.responseMessage ||
          "Failed to load report"
      );
    }
  } catch (e) {
    toast.error("Error loading report");
  } finally {
    setRepBusy(false);
  }
};

  const askToggle = (client) => {
    const current = String(client.status || "").toLowerCase() === "activate";
    const next = current ? "Deactivate" : "Activate";
    setConfirmClient(client);
    setConfirmNext(next);
    setConfirmOpen(true);
  };

  // actions: submit
 const submitTopUp = async () => {
  if (!topClient?.id) return;

  if (!topAmount || Number(topAmount) <= 0) {
    toast.error("Enter a valid amount");
    return;
  }

  try {
    setTopBusy(true);

    const data = await creditTopup({
      adminId,
      userId: topClient.id,
      topupAmount: topAmount,
      transactionReference: topRef,
      transactionRemarks: topRemark,
    });

    if (data?.responseCode === "00") {
      toast.success(
        data?.responseMessage || "Top up added"
      );
      setTopOpen(false);
      await fetchClients(); // refresh list
    } else {
      toast.error(
        data?.responseMessage ||
          "Failed to add top up"
      );
    }
  } catch (e) {
    toast.error("Error adding top up");
  } finally {
    setTopBusy(false);
  }
};

const submitToggle = async () => {
  if (!confirmClient?.id) return;

  try {
    setConfirmBusy(true);

    const data = await toggleClientStatus({
      userid: confirmClient.id,
      status: confirmNext, // "Activate" or "Deactivate"
    });

    if (data?.responseCode === "00") {
      toast.success(
        data?.responseMessage ||
          `Client ${confirmNext}d`
      );
      setConfirmOpen(false);
      await fetchClients();
    } else {
      toast.error(
        data?.responseMessage ||
          "Failed to update status"
      );
    }
  } catch (e) {
    toast.error("Error updating status");
  } finally {
    setConfirmBusy(false);
  }
};

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <BadgeDollarSign size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">White Labels</h1>
            <p className="text-sm text-gray-500">Activate clients, add top-ups, and view credit reports</p>
          </div>
        </div>
        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          onClick={fetchClients}
          disabled={loading}
        >
          <RefreshCcw size={16} /> {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 text-sm font-semibold text-gray-700">Filters</div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <input
              className="h-10 w-80 rounded-xl border px-3 text-sm outline-none"
              placeholder="Name / Mobile / User type / Onboarded by…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
            />
          </label>
          <div className="ml-auto flex gap-2">
            <button className="h-10 rounded-xl border px-3 text-sm" onClick={() => { setQ(""); setPage(1); }}>
              Clear
            </button>
            <button className="h-10 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white" disabled title="Filters auto-apply">
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full table-fixed text-left text-sm">
            <colgroup>
              <col className="w-16" />
              <col className="w-28" />
              <col className="w-[20%]" />
              <col className="w-[14%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[16%]" />
              <col className="w-[20%]" />
            </colgroup>
            <thead className="bg-[#01C37D] text-white">
              <tr className="whitespace-nowrap">
                {["ID","Activate","Client Name","Mobile","User Type","OB Type","OB By","Credit","Status","Created On","Actions"].map(h => (
                  <th key={h} className="px-4 py-2 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && pageRows.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500">No clients found.</td></tr>
              )}

              {pageRows.map((c) => {
                const isActive = String(c.status || "").toLowerCase() === "activate";
                return (
                  <tr key={c.id} className="border-t align-top">
                    <td className="px-4 py-2">{c.id}</td>
                    <td className="px-4 py-2">
                      <button
                        className={`inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs ${isActive ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-rose-50 border-rose-200 text-rose-700"}`}
                        onClick={() => askToggle(c)}
                      >
                        {isActive ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
                        {isActive ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                    <td className="px-4 py-2">{c.client_name}</td>
                    <td className="px-4 py-2">{c.client_mobile}</td>
                    <td className="px-4 py-2">{c.userType}</td>
                    <td className="px-4 py-2">{c.onboardingType}</td>
                    <td className="px-4 py-2">{c.onboardedBy || "N/A"}</td>
                    <td className="px-4 py-2">{c.credit}</td>
                    <td className="px-4 py-2">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          Deactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{c.createdOn}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                          onClick={() => openTopUp(c)}
                          title="Add TopUp"
                        >
                          Add TopUp
                        </button>
                        <button
                          className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                          onClick={() => openReport(c)}
                          title="View TopUp Report"
                        >
                          Report
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {loading && (
                <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
            <span className="text-sm text-gray-600">Page <b>{page}</b> of <b>{totalPages}</b></span>
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
          </div>
        )}
      </div>

      {/* ---- TopUp Modal ---- */}
      <Transition show={topOpen} as={Fragment}>
        <Dialog onClose={() => (!topBusy && setTopOpen(false))} className="relative z-50">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold">
                  Add TopUp {topClient ? `— ${topClient.client_name}` : ""}
                </Dialog.Title>
                <div className="mt-3 space-y-3">
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs text-gray-500">Amount</span>
                    <input
                      type="number"
                      min="1"
                      className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                      value={topAmount}
                      onChange={(e) => setTopAmount(e.target.value)}
                      placeholder="e.g., 1000"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs text-gray-500">Transaction Reference</span>
                    <input
                      className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
                      value={topRef}
                      onChange={(e) => setTopRef(e.target.value)}
                      placeholder="UTR / Ref no."
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs text-gray-500">Remarks</span>
                    <textarea
                      rows={3}
                      className="w-full rounded-xl border px-3 py-2 text-sm outline-none"
                      value={topRemark}
                      onChange={(e) => setTopRemark(e.target.value)}
                      placeholder="Optional"
                    />
                  </label>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setTopOpen(false)} disabled={topBusy}>Cancel</button>
                  <button
                    className="h-9 rounded-xl bg-[#01C37D] px-3 text-sm font-medium text-white hover:bg-[#01C37D]/90"
                    onClick={submitTopUp}
                    disabled={topBusy}
                  >
                    {topBusy ? "Adding…" : "Add TopUp"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ---- Report Modal (fixed height, scrolls inside) ---- */}
      <Transition show={repOpen} as={Fragment}>
        <Dialog onClose={() => setRepOpen(false)} className="relative z-50">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <Dialog.Title className="text-base font-semibold">
                    TopUp Report {repClient ? `— ${repClient.client_name}` : ""}
                  </Dialog.Title>
                  <button className="h-8 rounded-xl border px-3 text-sm" onClick={() => setRepOpen(false)}>Close</button>
                </div>
                <div className="max-h-[70vh] overflow-auto rounded-xl border">
                  <table className="min-w-full table-fixed text-left text-sm">
                    <colgroup>
                      <col className="w-16" />
                      <col className="w-24" />
                      <col className="w-40" />
                      <col className="w-40" />
                      <col className="w-40" />
                      <col className="w-40" />
                      <col className="w-[30%]" />
                    </colgroup>
                    <thead className="bg-[#01C37D] text-white">
                      <tr className="whitespace-nowrap">
                        {["ID","Amount","Transaction ID","Reference ID","Created On","Updated On","Remarks"].map(h => (
                          <th key={h} className="px-4 py-2 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {repBusy && (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
                      )}
                      {!repBusy && repRows.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No records</td></tr>
                      )}
                      {!repBusy && repRows.map((r) => (
                        <tr key={r.id} className="border-t align-top">
                          <td className="px-4 py-2">{r.id}</td>
                          <td className="px-4 py-2">{r.amount}</td>
                          <td className="px-4 py-2">{r.transaction_id || "N/A"}</td>
                          <td className="px-4 py-2">{r.reference_id}</td>
                          <td className="px-4 py-2">{r.created_on}</td>
                          <td className="px-4 py-2">{r.updated_on}</td>
                          <td className="px-4 py-2">{r.remarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* ---- Confirm Activate/Deactivate ---- */}
      <Transition show={confirmOpen} as={Fragment}>
        <Dialog onClose={() => (!confirmBusy && setConfirmOpen(false))} className="relative z-50">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <Dialog.Title className="text-base font-semibold">
                  {confirmNext} this client?
                </Dialog.Title>
                <p className="mt-2 text-sm text-gray-600">
                  Client: <span className="font-medium">{confirmClient?.client_name}</span><br />
                  Mobile: <span className="font-mono">{confirmClient?.client_mobile}</span>
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setConfirmOpen(false)} disabled={confirmBusy}>Cancel</button>
                  <button
                    className={`h-9 rounded-xl px-3 text-sm font-medium text-white ${confirmNext === "Activate" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
                    onClick={submitToggle}
                    disabled={confirmBusy}
                  >
                    {confirmBusy ? "Working…" : confirmNext}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
